import { useState, useMemo } from 'react';
import * as LucideIcons from 'lucide-react';
import { useTranslation } from "react-i18next";
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { ScrollArea } from '@/components/ui/scroll-area';
import { namespace } from "@common-config/constants";

type Props = {
    onSelect: (iconName: string) => void;
    currentIcon?: string;
}

export default function LucideIconPicker({ onSelect, currentIcon }: Props) {
    const { t } = useTranslation(namespace);
    const [search, setSearch] = useState('');

    const iconList = useMemo(() => {
        return Object.keys(LucideIcons)
            .filter(key => key !== 'createLucideIcon' && key !== 'default' && !key.endsWith('Icon')) // Filter out non-icon exports
            .filter(key => key.toLowerCase().includes(search.toLowerCase()));
    }, [search]);

    // Limit displayed icons to improve performance, but allow searching to find any
    const displayList = iconList.slice(0, 800); 

    return (
        <div className="w-[320px] p-2 flex flex-col gap-2">
            <Input
                placeholder={t('labels.searchIcons')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-8"
                autoFocus
            />
            <ScrollArea 
                className="h-[300px] pr-3"
                onWheel={(e) => e.stopPropagation()}
                onTouchMove={(e) => e.stopPropagation()}
            >
                <div className="grid grid-cols-6 gap-1 p-1">
                    {displayList.map((iconName) => {
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        const Icon = (LucideIcons as any)[iconName];
                        if (!Icon) return null;

                        const isSelected = currentIcon === iconName;

                        return (
                            <button
                                key={iconName}
                                onClick={() => onSelect(iconName)}
                                title={iconName}
                                className={cn(
                                    "flex items-center justify-center p-2 rounded-md hover:bg-gray-100 transition-colors",
                                    isSelected && "bg-blue-100 text-blue-600 ring-2 ring-blue-500 ring-offset-1"
                                )}
                            >
                                <Icon size={20} />
                            </button>
                        );
                    })}
                    {iconList.length > 800 && (
                         <div className="col-span-6 text-center text-xs text-gray-400 py-2">
                            {t('labels.useSearchToFindMore')}
                        </div>
                    )}
                    {displayList.length === 0 && (
                        <div className="col-span-6 text-center text-sm text-gray-500 py-4">
                            {t('labels.noIconsFound')}
                        </div>
                    )}
                </div>
            </ScrollArea>
        </div>
    );
}
